import React, { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import Badge from 'components/core/Badge';
import Button from 'components/core/Button';
import Card from 'components/core/Card';
import Chip, { ChipList } from 'components/core/Chip';

import { useGetSegments } from 'api/audience';
import { TSegment } from 'types/segment';

import {
  StyledActions,
  StyledCreate,
  StyledFooterRow,
  StyledList,
  StyledMeta,
  StyledNotice,
  StyledRow,
  StyledRowMain,
  StyledSearch,
  StyledSummaryHead,
  StyledSummaryMain,
  StyledTitle,
} from './AudiencePicker.styles';

type TAudiencePickerProps = {
  value?: string | null;
  /** Slide placeholders the current selection cannot satisfy; the modal gates its save on these. */
  missingKeys?: string[];
  onChange: (segmentId: string | undefined) => void;
};

const SegmentMeta = ({ segment }: { segment: TSegment }) => (
  <>
    <Badge tone={segment.merchantCount > 0 ? 'success' : 'neutral'} size="sm">
      {segment.merchantCount.toLocaleString()} merchant{segment.merchantCount === 1 ? '' : 's'}
    </Badge>
    {segment.attributeKeys.length ? (
      <ChipList>
        {segment.attributeKeys.map((key) => (
          <Chip key={key} size="sm">
            {key}
          </Chip>
        ))}
      </ChipList>
    ) : null}
  </>
);

const AudiencePicker = ({ value, missingKeys = [], onChange }: TAudiencePickerProps) => {
  const { segments } = useGetSegments();
  const navigate = useNavigate();
  const [isPicking, setIsPicking] = useState(false);
  const [search, setSearch] = useState('');

  const selected = value ? segments.find((segment) => segment.id === value) : undefined;

  const choose = (id: string | undefined) => {
    onChange(id);
    setIsPicking(false);
    setSearch('');
  };

  if (isPicking) {
    const list = segments.filter((segment) =>
      segment.key.toLowerCase().includes(search.trim().toLowerCase())
    );

    return (
      <Card variant="tertiary">
        <StyledSearch
          value={search}
          placeholder="Search segments…"
          onChange={(event) => setSearch(event.target.value)}
        />
        <StyledList>
          <StyledRow type="button" $active={!value} onClick={() => choose(undefined)}>
            <StyledRowMain>
              <StyledTitle>All merchants</StyledTitle>
              <StyledMeta>Everyone sees this group</StyledMeta>
            </StyledRowMain>
          </StyledRow>
          {list.map((segment) => (
            <StyledRow
              key={segment.id}
              type="button"
              $active={segment.id === value}
              onClick={() => choose(segment.id)}>
              <StyledRowMain>
                <StyledTitle>{segment.key}</StyledTitle>
                <SegmentMeta segment={segment} />
              </StyledRowMain>
            </StyledRow>
          ))}
          {list.length === 0 ? (
            <StyledMeta>No segments match — create one below.</StyledMeta>
          ) : null}
        </StyledList>
        <StyledFooterRow>
          <StyledCreate type="button" onClick={() => navigate({ to: '/audiences/new' })}>
            + New segment
          </StyledCreate>
          <Button variant="ghost" size="sm" onClick={() => setIsPicking(false)}>
            Close
          </Button>
        </StyledFooterRow>
      </Card>
    );
  }

  return (
    <Card variant="tertiary">
      <StyledSummaryHead>
        <StyledSummaryMain>
          {value && !selected ? <StyledTitle>Segment no longer exists</StyledTitle> : null}
          {!value ? (
            <>
              <StyledTitle>All merchants</StyledTitle>
              <StyledMeta>Everyone sees this group</StyledMeta>
            </>
          ) : null}
          {selected ? (
            <>
              <StyledTitle>{selected.key}</StyledTitle>
              <SegmentMeta segment={selected} />
            </>
          ) : null}
        </StyledSummaryMain>
        <StyledActions>
          {value ? (
            <Button variant="ghost" size="sm" onClick={() => choose(undefined)}>
              Clear
            </Button>
          ) : null}
          <Button variant="secondary" size="sm" onClick={() => setIsPicking(true)}>
            Change
          </Button>
        </StyledActions>
      </StyledSummaryHead>

      {selected && selected.merchantCount === 0 ? (
        <StyledNotice $tone="warn">
          ⚠ This segment has no members yet — it won’t reach anyone until the next sync resolves
          {selected.lastSyncedAt ? ' more merchants' : ' its first run'}.
        </StyledNotice>
      ) : null}
      {missingKeys.length ? (
        <StyledNotice $tone="danger">
          Slides use {missingKeys.map((key) => `{${key}}`).join(', ')} —{' '}
          {selected
            ? `this segment has no matching attribute${missingKeys.length > 1 ? 's' : ''}.`
            : 'assign an audience that provides them, or remove them from the slides.'}
        </StyledNotice>
      ) : null}
    </Card>
  );
};

export default AudiencePicker;
